'use client'
import { type FC, useCallback, useMemo } from 'react'
import { Badge, HStack, Stack, VStack } from '@chakra-ui/react'
import { MdRemove } from 'react-icons/md'
import { GrandIconButton, GrandText, ImageBox, GrandLink } from 'ui'
import { getProductImages } from 'shared-utils/utils/products'
import { type ICartItemSingle } from 'ecosystem'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'
import ProductPriceComponent from '../../../../../products/components/ProductPrice'
import { QuantityDiscountTeaser } from '../../../../../products/components/QuantityDiscountTeaser'
import CheckoutCartSubItems from '../CheckoutCartSubItems'
import { type CheckoutCartItemProps } from '../types'
import { useCheckoutContext } from '../../../../hooks'
import { CheckoutCartServices } from '../CheckoutCartServices'
import { CheckoutCartItemCounter } from '../CheckoutCartItemCounter'

export type CheckoutCartItemSingleProps = Omit<CheckoutCartItemProps, 'cartItem'> & {
  cartItem: ICartItemSingle
}

export const CheckoutCartItemSingle: FC<CheckoutCartItemSingleProps> = ({
  cartItem,
  onServicesIdsUpdate,
  size,
  onDelete,
  onUpdate,
  isEditable = true
}) => {
  const qty = cartItem.qty || 1
  const { product, subItems } = cartItem.meta.singleType
  const { services: allAvailableServices } = useCheckoutContext()
  const { mainImageUrl } = getProductImages(product.medias)
  const { appPaths } = useUIGlobalsContext()

  const services = useMemo(
    () => allAvailableServices?.filter((service) => !!product.serviceIds?.includes(service.id)),
    [allAvailableServices, product.serviceIds]
  )

  const onQtyChange = useCallback(
    (newQty: number) => {
      onUpdate({
        ...cartItem,
        qty: newQty
      })
    },
    [cartItem, onUpdate]
  )

  const getSize = () => {
    const dict = {
      sm: {
        w: {
          base: 10,
          md: 12
        },
        h: {
          base: 10,
          md: 12
        }
      },
      md: {
        w: { base: 18, md: 20 },
        h: { base: 18, md: 20 }
      },
      lg: {
        w: { base: '16vw', md: 28 },
        h: { base: '16vw', md: 28 }
      }
    }

    return dict[size || 'sm']
  }

  return (
    <VStack
      borderColor="gray.100"
      borderWidth={1}
      p={4}
      spacing={4}
      w="100%"
      alignItems="flex-start"
    >
      {cartItem.offerInfo?.name && (
        <Badge
          fontSize={{
            base: '2xs',
            sm: 'xs'
          }}
          backgroundColor="primary"
          color="text.light"
          px={2}
        >
          {cartItem.offerInfo.name}
        </Badge>
      )}

      <Stack direction={{ base: 'column', sm: 'row' }} w="100%">
        <GrandText
          display={{ base: 'initial', sm: 'none' }}
          fontSize={{
            base: 'xs',
            md: 'sm'
          }}
          isTruncated
        >
          <GrandLink href={`${appPaths.productsUrlPath}/${cartItem.meta.singleType.product.slug}`}>
            {product.name}
          </GrandLink>
        </GrandText>

        <HStack gap={4} w="full">
          <GrandLink href={`${appPaths.productsUrlPath}/${cartItem.meta.singleType.product.slug}`}>
            <ImageBox
              alignSelf="flex-start"
              imageProps={{
                alt: product.name,
                title: product.name,
                src: mainImageUrl,
                style: {
                  objectFit: 'contain'
                },
                sizes: '7em'
              }}
              {...getSize()}
            />
          </GrandLink>
          <VStack flex={1} w="full">
            <HStack
              justifyContent={{
                base: 'flex-end',
                sm: 'initial'
              }}
              w="100%"
            >
              <GrandText display={{ base: 'none', sm: 'initial' }} flexGrow={1} w="auto">
                <GrandLink
                  href={`${appPaths.productsUrlPath}/${cartItem.meta.singleType.product.slug}`}
                >
                  {product.name}
                </GrandLink>
              </GrandText>

              <HStack align="center">
                <ProductPriceComponent
                  price={product.price}
                  originalPrice={product.originalPrice}
                  priceWithoutVat={product.priceWithoutVat}
                  originalPriceWithoutVat={product.originalPriceWithoutVat}
                  priceProps={{
                    color: 'inherit'
                  }}
                  alignItems="baseline"
                  whiteSpace="nowrap"
                  mr={2}
                />

                {isEditable ? (
                  <CheckoutCartItemCounter
                    onChange={(_valueAsString, valueAsNumber) => {
                      onQtyChange(valueAsNumber)
                    }}
                    value={qty}
                  />
                ) : (
                  <GrandText fontSize="xs" w="auto">
                    {qty}x
                  </GrandText>
                )}

                <GrandIconButton
                  aria-label="Ta bort"
                  bg="status.error"
                  color="text.light"
                  flexShrink={0}
                  fontSize="xs"
                  h={4}
                  icon={<MdRemove />}
                  onClick={() => {
                    onDelete(cartItem)
                  }}
                  shadow="none"
                  size="xs"
                  tooltip="Ta bort"
                  w={4}
                />
              </HStack>
            </HStack>
          </VStack>
        </HStack>
      </Stack>

      {/* Last place the shopper can still act on the offer, and the stepper is
          right there — so the copy re-evaluates live as they change the
          quantity, the same way the PDP teaser tracks its counter. */}
      <QuantityDiscountTeaser product={product} quantity={qty} surface="checkout" />

      {!!subItems?.length && <CheckoutCartSubItems subItems={subItems} />}

      {services?.length && (
        <CheckoutCartServices
          services={services}
          onChange={(servicesIds) => {
            onServicesIdsUpdate(product.id, servicesIds)
          }}
        />
      )}
    </VStack>
  )
}
